import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import {
  MAX_STRUCTURE_DEPTH,
  TenantAttributeType,
  TenantAttributeTypeI18N,
  type TenantAttributeSpec,
} from "@the-real-insight/tri-model";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * One level of an attribute definition as it is being typed: name, type, unit or values — and for a
 * structure its fields, each again a `SpecEditor`, down to the depth the model allows.
 *
 * The values of an enumeration stay the text of the textarea until the dialog checks the whole
 * definition; splitting them on every keystroke would eat the line the reader is typing.
 */

export interface SpecDraft {
  /** Stable while editing, so removing one field keeps the others' state. */
  key: string;
  name: string;
  valueType: TenantAttributeType;
  unit: string;
  values: string;
  fields: SpecDraft[];
}

let nextKey = 0;
const newKey = () => `field-${++nextKey}`;

export const emptyDraft = (): SpecDraft => ({
  key: newKey(),
  name: "",
  valueType: TenantAttributeType.text,
  unit: "",
  values: "",
  fields: [],
});

export const draftOf = (spec: TenantAttributeSpec): SpecDraft => ({
  key: newKey(),
  name: spec.name,
  valueType: spec.valueType,
  unit: spec.unit ?? "",
  values: (spec.allowedValues ?? []).join("\n"),
  fields: (spec.fields ?? []).map(draftOf),
});

/** The draft in the shape `sanitizeTenantAttributeSpec` reads. */
export const rawOf = (draft: SpecDraft): Record<string, unknown> => ({
  name: draft.name,
  valueType: draft.valueType,
  unit: draft.unit,
  allowedValues: draft.values.split("\n"),
  fields: draft.fields.map(rawOf),
});

const NUMERIC = [TenantAttributeType.integer, TenantAttributeType.decimal];

interface Properties {
  draft: SpecDraft;
  onChange: (draft: SpecDraft) => void;
  /** 1 for the attribute itself, 2 for its fields, … */
  depth: number;
  onRemove?: () => void;
}

export default function SpecEditor({ draft, onChange, depth, onRemove }: Properties) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  const fieldId = useId();
  const nested = depth > 1;
  // A structure at the last level could hold nothing but primitives — the type is not offered there.
  const types = Object.values(TenantAttributeType).filter(
    (type) => type !== TenantAttributeType.structure || depth < MAX_STRUCTURE_DEPTH,
  );

  const set = (patch: Partial<SpecDraft>) => onChange({ ...draft, ...patch });

  const setType = (valueType: TenantAttributeType) =>
    // A new structure starts with one empty field, so there is something to fill in at once.
    set({ valueType, fields: valueType === TenantAttributeType.structure && draft.fields.length === 0 ? [emptyDraft()] : draft.fields });

  const setField = (index: number, field: SpecDraft) =>
    set({ fields: draft.fields.map((entry, position) => (position === index ? field : entry)) });

  return (
    <div className={cn("flex flex-col gap-3", nested && "rounded-md border bg-muted/30 p-3")}>
      <div className="flex items-end gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-name`}>
            {nested ? t("operatorObligationsPanel.dataModel.attribute.fieldName") : t("operatorObligationsPanel.dataModel.attribute.name")}
          </Label>
          <Input id={`${fieldId}-name`} value={draft.name} maxLength={80} onChange={(event) => set({ name: event.target.value })} />
        </div>
        <div className="flex w-[160px] shrink-0 flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-type`}>{t("operatorObligationsPanel.dataModel.attribute.type")}</Label>
          <Select value={draft.valueType} onValueChange={(value) => setType(value as TenantAttributeType)}>
            <SelectTrigger id={`${fieldId}-type`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {types.map((type) => (
                <SelectItem key={type} value={type}>
                  {TenantAttributeTypeI18N[type][lang]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {onRemove ? (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
            aria-label={t("operatorObligationsPanel.dataModel.attribute.removeField", { name: draft.name || "…" })}
            onClick={onRemove}
          >
            <Trash2 size={14} />
          </Button>
        ) : null}
      </div>

      {NUMERIC.includes(draft.valueType) ? (
        <div className="flex w-[200px] max-w-full flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-unit`}>{t("operatorObligationsPanel.dataModel.attribute.unit")}</Label>
          <Input
            id={`${fieldId}-unit`}
            value={draft.unit}
            maxLength={30}
            placeholder={t("operatorObligationsPanel.dataModel.attribute.unitPlaceholder")}
            onChange={(event) => set({ unit: event.target.value })}
          />
        </div>
      ) : null}

      {draft.valueType === TenantAttributeType.enumeration ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-values`}>{t("operatorObligationsPanel.dataModel.attribute.values")}</Label>
          <Textarea
            id={`${fieldId}-values`}
            rows={4}
            value={draft.values}
            placeholder={t("operatorObligationsPanel.dataModel.attribute.valuesPlaceholder")}
            onChange={(event) => set({ values: event.target.value })}
          />
          <p className="text-xs text-muted-foreground">{t("operatorObligationsPanel.dataModel.attribute.valuesHint")}</p>
        </div>
      ) : null}

      {draft.valueType === TenantAttributeType.structure ? (
        <div className="flex flex-col gap-2 border-l-2 border-primary/30 pl-3">
          <p className="text-xs font-semibold text-muted-foreground">{t("operatorObligationsPanel.dataModel.attribute.fields")}</p>
          {draft.fields.map((field, index) => (
            <SpecEditor
              key={field.key}
              draft={field}
              depth={depth + 1}
              onChange={(next) => setField(index, next)}
              onRemove={() => set({ fields: draft.fields.filter((_, position) => position !== index) })}
            />
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="w-fit gap-1.5"
            onClick={() => set({ fields: [...draft.fields, emptyDraft()] })}
          >
            <Plus size={14} aria-hidden />
            {t("operatorObligationsPanel.dataModel.attribute.addField")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
